import { useFormik } from 'formik'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { Form, FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'
import { Select } from 'sutra/components/Select/Select'
import * as Yup from 'yup'

import { CreateAccountGroupModal } from 'features/account-create/components/CreateAccountGroupModal/CreateAccountGroupModal'
import { useAccountGroups } from 'features/account-create/hooks/use-account-groups'
import { useCreateAccount } from 'features/account-create/hooks/use-create-account'
import { useCreateAccountGroup } from 'features/account-create/hooks/use-create-account-group'
import { useAccountsService } from 'features/accounts/service'
import type { Account } from 'features/accounts/types'

type Props = {
  open: boolean
  onClose: () => void
  onCreated?: (account: Account) => void
}

const CREATE_GROUP_VALUE = '__create_account_group__'

type CreateAccountFormValues = {
  name: string
  salesforce_id: string
  account_group_uuid: string
}

const initialValues: CreateAccountFormValues = {
  name: '',
  salesforce_id: '',
  account_group_uuid: '',
}

const CreateAccountSchema = Yup.object().shape({
  name: Yup.string().trim().required('A name is required'),
  salesforce_id: Yup.string().trim().required('A Salesforce ID is required'),
  account_group_uuid: Yup.string().required('An account group is required'),
})

const toastError = (e: unknown): void => {
  toast.error(
    (e as { response?: { data?: { detail?: string } }; message?: string })
      ?.response?.data?.detail || (e as Error).message,
  )
}

export const CreateAccountModalContainer = ({
  open,
  onClose,
  onCreated,
}: Props): JSX.Element => {
  const service = useAccountsService()
  const { accountGroups, isLoading: isLoadingGroups } = useAccountGroups(
    service,
    open,
  )
  const { createAccount, isCreating } = useCreateAccount(service)
  const { createAccountGroup, isCreating: isCreatingGroup } =
    useCreateAccountGroup(service)
  const [isCreateGroupModalOpen, setCreateGroupModalOpen] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [groupNameError, setGroupNameError] = useState<string | null>(null)

  const formik = useFormik({
    initialValues,
    validationSchema: CreateAccountSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      try {
        const account = await createAccount({
          name: values.name.trim(),
          salesforce_id: values.salesforce_id.trim(),
          account_group_uuid: values.account_group_uuid,
        })
        resetForm()
        onCreated?.(account)
        onClose()
      } catch (e: unknown) {
        toastError(e)
      }
    },
  })

  const handleCancel = (): void => {
    formik.resetForm()
    onClose()
  }

  const closeCreateGroupModal = (): void => {
    setCreateGroupModalOpen(false)
    setGroupName('')
    setGroupNameError(null)
  }

  const handleCreateGroup = async (): Promise<void> => {
    const name = groupName.trim()
    if (!name) {
      setGroupNameError('A name is required')
      return
    }
    try {
      const group = await createAccountGroup({ name })
      await formik.setFieldValue('account_group_uuid', group.uuid)
      closeCreateGroupModal()
    } catch (e: unknown) {
      toastError(e)
    }
  }

  return (
    <PopoverModal
      title="Create account"
      isOpen={open}
      onOk={() => void formik.submitForm()}
      onClose={handleCancel}
      okText="Create"
      okButtonProps={{ loading: isCreating }}
      width={520}
      content={
        <>
          <Form layout="vertical">
            <FormItem
              label="Name"
              required
              validateStatus={formik.errors.name ? 'error' : ''}
              help={formik.errors.name}
            >
              <Input
                name="name"
                $error={!!formik.errors.name}
                placeholder="Account name"
                value={formik.values.name}
                onChange={formik.handleChange}
              />
            </FormItem>
            <FormItem
              label="Salesforce ID"
              required
              validateStatus={formik.errors.salesforce_id ? 'error' : ''}
              help={formik.errors.salesforce_id}
            >
              <Input
                name="salesforce_id"
                $error={!!formik.errors.salesforce_id}
                placeholder="Salesforce ID"
                value={formik.values.salesforce_id}
                onChange={formik.handleChange}
              />
            </FormItem>
            <FormItem
              label="Account group"
              required
              validateStatus={formik.errors.account_group_uuid ? 'error' : ''}
              help={formik.errors.account_group_uuid}
            >
              <Select
                placeholder="Select an account group"
                loading={isLoadingGroups}
                value={formik.values.account_group_uuid || undefined}
                onChange={(uuid) => {
                  if (uuid === CREATE_GROUP_VALUE) {
                    setCreateGroupModalOpen(true)
                    return
                  }
                  void formik.setFieldValue('account_group_uuid', uuid)
                }}
                options={[
                  ...accountGroups.map((group) => ({
                    label: group.name,
                    value: group.uuid,
                  })),
                  // An action row, not a group: picking it opens the create modal
                  // and never becomes the field's value.
                  {
                    label: 'Create new account group',
                    value: CREATE_GROUP_VALUE,
                  },
                ]}
              />
            </FormItem>
          </Form>
          <CreateAccountGroupModal
            open={isCreateGroupModalOpen}
            name={groupName}
            isCreating={isCreatingGroup}
            errorMessage={groupNameError}
            onNameChange={setGroupName}
            onCreate={() => void handleCreateGroup()}
            onClose={closeCreateGroupModal}
          />
        </>
      }
    />
  )
}
